import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import { Box, Flex } from 'rebass';
import { TextFieldStyled } from '@nike/nike-design-system-components';
import { func, bool, string, object } from 'prop-types';
import styled from '@emotion/styled';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import { trimNSplit } from '../../../util/trim';
import {
  createProductContractGroups as createProductContractGroupsAction,
  requestContractsById,
  updateProductContractGroup as updateProductContractGroupAction
} from '../actions';
import Modal from '../../../modal/Modal';

import { Button, InputContainer, P } from '../../../components';
import TextInput from '../../../components/formInputs/TextInput';
import FilterTag from '../../../components/filters/FilterTag';

import { validationSchema } from '../../constants';

export const ContractNumbersContainer = styled.div`
  max-height: 400px;
  overflow-y: scroll;
`;

const ContractGroupModal = ({
  actionName,
  closeModal,
  createProductContractGroups,
  isOpen,
  productContractGroup,
  requestContractsById,
  title,
  type,
  updateProductContractGroup,
  handleAllContractsCount
}) => {
  const { refreshTableData } = useContext(TableServerSideContext);

  const [validContracts, setValidContracts] = useState(new Set());
  const [allContracts, setAllContracts] = useState(new Set());
  const [validate, setValidate] = useState(false);
  const createModalType = type === 'create';

  const initialValues = { contracts: '', name: '' };

  const { action, query } = {
    add: {
      action: updateProductContractGroupAction,
      query: updateProductContractGroup
    },
    create: {
      action: createProductContractGroupsAction,
      query: createProductContractGroups
    }
  }[type];

  const onSubmit = async ({ name }, { resetForm }) => {
    let result;

    if (createModalType) {
      result = await query({
        name,
        contracts: [...allContracts].filter(c => validContracts.has(c))
      });

      refreshTableData();
    } else if (type === 'add') {
      result = await query({
        productContractGroupId: productContractGroup?.id,
        data: {
          contracts: [...validContracts, ...productContractGroup?.contracts?.filter(c => !validContracts.has(c))]
        }
      });
    }

    if (result?.type !== action.event.error) {
      resetForm(initialValues);

      setAllContracts(new Set());
      setValidContracts(new Set());
    }

    closeModal();
  };

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isValid,
    isSubmitting,
    setFieldValue,
    touched,
    values: { contracts, name }
  } = useFormik({
    initialValues,
    onSubmit,
    ...(createModalType ? { validationSchema } : {})
  });

  const handleCloseModal = () => {
    closeModal();
    setAllContracts(new Set());
  };

  const handleInputChange = async e => {
    if (e.key === 'Enter') {
      const additionalContracts = trimNSplit(e.target?.value)
        .map(c => c.padStart(10, '0'))
        .filter(c => !allContracts.has(c));
      const contractList = [...allContracts, ...additionalContracts];

      const contracts = await requestContractsById(contractList);

      setAllContracts(new Set(contractList));
      setValidContracts(new Set(contracts.map(({ sourceContractId: id }) => id)));

      setFieldValue('contracts', '');
    }
  };

  useEffect(() => {
    if (validate) {
      handleInputChange({ key: 'Enter', target: { value: '' } });
      setValidate(false);
    }

    handleAllContractsCount([...allContracts]);
  }, [allContracts]);

  const content = () => (
    <Flex flexDirection='column' justifyContent='center'>
      {createModalType && (
        <>
          <TextFieldStyled
            id='name'
            name='name'
            label='Name'
            value={name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.name && touched.name}
            errorMessage={errors.name}
            inputProps={{ style: { textTransform: 'uppercase' } }}
          />
          <Box my={2} />
        </>
      )}
      <P className='mb5-sm'>Enter contract numbers to associate them with this group.</P>
      <ContractNumbersContainer>
        <div>
          {[...allContracts].map(id => (
            <FilterTag
              key={`filterTag__${id}`}
              id={`filterTag__${id}`}
              error={!validContracts.has(id)}
              onClick={() => {
                setAllContracts(contracts => new Set([...contracts].filter(c => c !== id)));
                setValidate(true);
              }}
            >
              {id}
            </FilterTag>
          ))}
        </div>
      </ContractNumbersContainer>
      <InputContainer label='Contract Numbers' customStyles={{ width: '100%' }}>
        <TextInput
          name='contracts'
          value={contracts}
          onChange={e => setFieldValue('contracts', e)}
          onKeyPress={handleInputChange}
        />
      </InputContainer>
    </Flex>
  );

  const disabledAction = isSubmitting || !isValid || (!validContracts.size && !createModalType);

  const footer = () => (
    <Flex justifyContent='center'>
      <Button
        data-testid='add_create_contracts'
        onClick={handleSubmit}
        extraClassName='ncss-btn-primary-dark'
        disabled={disabledAction}
      >
        {actionName}
      </Button>
    </Flex>
  );

  return (
    <Modal title={title} content={content} footer={footer} isOpen={isOpen} closeModal={handleCloseModal} unwrapped />
  );
};

ContractGroupModal.defaultProps = {
  productContractGroup: {},
  handleAllContractsCount: () => {}
};

ContractGroupModal.propTypes = {
  actionName: string.isRequired,
  closeModal: func.isRequired,
  createProductContractGroups: func.isRequired,
  isOpen: bool.isRequired,
  productContractGroup: object,
  requestContractsById: func.isRequired,
  title: string.isRequired,
  type: string.isRequired,
  updateProductContractGroup: func.isRequired,
  handleAllContractsCount: func
};

export default connect(null, {
  createProductContractGroups: createProductContractGroupsAction,
  requestContractsById,
  updateProductContractGroup: updateProductContractGroupAction
})(ContractGroupModal);
